import React, { forwardRef, useEffect, useRef } from 'react';
import styled from 'styled-components';

const Container = styled.div`
  position: relative;
  width: ${props => props.width}px;
  height: ${props => props.height}px;
  overflow: hidden;
`;

const Canvas = styled.canvas`
  position: absolute;
  top: 0;
  left: 0;
  touch-action: none;
`;

const ScratchCardCanvas = forwardRef(({ width, height, brushSize, onComplete, prize }, ref) => {
  const prizeCanvasRef = useRef(null);
  const scratchCanvasRef = useRef(null);
  const isDragging = useRef(false);
  const lastPoint = useRef(null);

  useEffect(() => {
    // Canvas del premio (fondo)
    const prizeCtx = prizeCanvasRef.current.getContext('2d');
    prizeCtx.fillStyle = '#ffffff';
    prizeCtx.fillRect(0, 0, width, height);
    
    // Configurar el texto del premio
    prizeCtx.font = 'bold 24px Arial';
    prizeCtx.fillStyle = '#4a5568';
    prizeCtx.textAlign = 'center';
    prizeCtx.textBaseline = 'middle';

    // Calcular el ancho máximo disponible con el padding
    const maxWidth = width - 40; // 20px de padding en cada lado

    // Dividir el texto en líneas si es necesario
    const words = prize.split(' ');
    let lines = [];
    let currentLine = words[0];

    for (let i = 1; i < words.length; i++) {
      const testLine = currentLine + ' ' + words[i];
      const metrics = prizeCtx.measureText(testLine);
      
      if (metrics.width > maxWidth) {
        lines.push(currentLine);
        currentLine = words[i];
      } else {
        currentLine = testLine;
      }
    }
    lines.push(currentLine);

    // Calcular altura total del texto
    const lineHeight = 30;
    const totalHeight = lineHeight * lines.length;
    const startY = (height - totalHeight) / 2 + lineHeight / 2;

    // Dibujar cada línea
    lines.forEach((line, index) => {
      prizeCtx.fillText(
        line,
        width / 2,
        startY + (index * lineHeight)
      );
    });

    // Canvas para rascar (capa superior)
    const scratchCtx = scratchCanvasRef.current.getContext('2d');
    scratchCtx.fillStyle = '#a19f9f';
    scratchCtx.fillRect(0, 0, width, height);
  }, [width, height, prize]);

  const getPosition = (event) => {
    const rect = scratchCanvasRef.current.getBoundingClientRect();
    const x = (event.clientX || event.touches[0].clientX) - rect.left;
    const y = (event.clientY || event.touches[0].clientY) - rect.top;
    return { x, y };
  };

  const scratch = (point) => {
    const ctx = scratchCanvasRef.current.getContext('2d');
    ctx.globalCompositeOperation = 'destination-out';

    // Función para decidir si un pixel se rasca o no
    const shouldScratch = () => Math.random() > 0.4; // 30% de probabilidad de no rascar

    // Patrón de rascado irregular con huecos
    for (let i = 0; i < 12; i++) {
      if (!shouldScratch()) continue; // Saltamos algunos puntos

      const randomX = point.x + (Math.random() - 0.5) * brushSize;
      const randomY = point.y + (Math.random() - 0.5) * brushSize;
      const randomSize = (Math.random() * 0.4 + 0.2) * brushSize / 2;
      const randomOpacity = Math.random() * 0.6 + 0.2; // Más variación en la opacidad

      ctx.beginPath();
      ctx.arc(randomX, randomY, randomSize, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(0, 0, 0, ${randomOpacity})`;
      ctx.fill();
    }

    // Línea entre puntos con huecos
    if (lastPoint.current) {
      const distance = Math.hypot(point.x - lastPoint.current.x, point.y - lastPoint.current.y);
      const steps = Math.floor(distance / (brushSize / 6)); // Más puntos para mejor distribución
      
      for (let i = 0; i < steps; i++) {
        if (!shouldScratch()) continue; // Saltamos algunos puntos en la línea

        const t = i / steps;
        const x = lastPoint.current.x + (point.x - lastPoint.current.x) * t;
        const y = lastPoint.current.y + (point.y - lastPoint.current.y) * t;
        
        // Puntos aleatorios a lo largo de la línea con huecos
        for (let j = 0; j < 6; j++) {
          if (!shouldScratch()) continue; // Más probabilidad de huecos

          const randomX = x + (Math.random() - 0.5) * brushSize;
          const randomY = y + (Math.random() - 0.5) * brushSize;
          const randomSize = (Math.random() * 0.4 + 0.2) * brushSize / 2;
          const randomOpacity = Math.random() * 0.6 + 0.2;

          ctx.beginPath();
          ctx.arc(randomX, randomY, randomSize, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(0, 0, 0, ${randomOpacity})`;
          ctx.fill();
        }
      }
    }

    lastPoint.current = point;
    calculateRevealed();
  };

  const calculateRevealed = () => {
    const ctx = scratchCanvasRef.current.getContext('2d');
    const imageData = ctx.getImageData(0, 0, width, height);
    const pixels = imageData.data;
    let transparent = 0;

    for (let i = 0; i < pixels.length; i += 4) {
      if (pixels[i + 3] < 128) transparent++;
    }

    const percentageRevealed = (transparent / (pixels.length / 4)) * 100;
    if (percentageRevealed > 30) {
      onComplete(percentageRevealed);
    }
  };

  const handleStart = (e) => {
    isDragging.current = true;
    scratch(getPosition(e));
  };

  const handleMove = (e) => {
    if (!isDragging.current) return;
    e.preventDefault();
    scratch(getPosition(e));
  };

  const handleEnd = () => {
    isDragging.current = false;
    lastPoint.current = null;
  };

  return (
    <Container width={width} height={height}>
      <Canvas ref={prizeCanvasRef} width={width} height={height} />
      <Canvas
        ref={scratchCanvasRef}
        width={width}
        height={height}
        onMouseDown={handleStart}
        onMouseMove={handleMove}
        onMouseUp={handleEnd}
        onMouseLeave={handleEnd}
        onTouchStart={handleStart}
        onTouchMove={handleMove}
        onTouchEnd={handleEnd}
      />
    </Container>
  );
});

export default ScratchCardCanvas; 